import React, { useEffect, useState } from 'react';
import { useNotification } from '../../../../context/NotificationContext';
import {
  getPlatformTextableSettings,
  setPlatformTextableSettings,
  testPlatformTextableSettings,
  clearPlatformTextableSettings,
} from './textableCredentialsApi';
const EMPTY_FORM = {
  apiBaseUrl: '',
  userUid: '',
  apiToken: '',
  fromNumber: '',
};

const TextableCredentialsCard = () => {
  const { showToast } = useNotification();
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState({ configured: false, tokenLast4: '' });
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('idle');
  const [error, setError] = useState('');

  const applyStatus = (data) => {
    setStatus({
      configured: Boolean(data?.configured),
      tokenLast4: data?.tokenLast4 || '',
    });
    setForm((prev) => ({
      ...prev,
      userUid: '',
      apiToken: '',
      apiBaseUrl: data?.apiBaseUrl || '',
      fromNumber: data?.fromNumber || '',
    }));
  };

  useEffect(() => {
    let cancelled = false;
    getPlatformTextableSettings()
      .then((data) => {
        if (!cancelled) applyStatus(data);
      })
      .catch((err) => {
        if (!cancelled) {
          const message = err?.message || 'Failed to load Textable settings';
          setError(message);
          showToast(message, 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setAction('saving');
    setError('');
    showToast('Saving Textable credentials…', 'info');
    try {
      const data = await setPlatformTextableSettings({
        apiBaseUrl: form.apiBaseUrl.trim(),
        fromNumber: form.fromNumber.trim(),
        userUid: form.userUid.trim(),
        apiToken: form.apiToken.trim(),
      });
      applyStatus(data);
      showToast('Textable credentials saved.', 'success');
    } catch (err) {
      const message = err?.message || 'Failed to save Textable settings';
      setError(message);
      showToast(message, 'error');
    } finally {
      setAction('idle');
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setLoading(true);
    setAction('testing');
    setError('');
    showToast('Sending test SMS…', 'info');
    try {
      const data = await testPlatformTextableSettings();
      showToast(`Test SMS sent to ${data?.to || 'your admin phone'}.`, 'success');
    } catch (err) {
      const message = err?.message || 'Textable test failed';
      setError(message);
      showToast(message, 'error');
    } finally {
      setAction('idle');
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Remove the stored Textable credentials? SMS sending will stop until you save new credentials.')) {
      return;
    }
    setLoading(true);
    setAction('clearing');
    setError('');
    showToast('Removing Textable credentials…', 'info');
    try {
      await clearPlatformTextableSettings();
      applyStatus({ configured: false, apiBaseUrl: '', fromNumber: '', tokenLast4: '' });
      setForm(EMPTY_FORM);
      showToast('Textable credentials removed.', 'success');
    } catch (err) {
      const message = err?.message || 'Failed to disconnect Textable';
      setError(message);
      showToast(message, 'error');
    } finally {
      setAction('idle');
      setLoading(false);
    }
  };

  return (
    <section className="admin-card app-settings-card platform-credentials-card">
      <div className="platform-credentials-card__header">
        <div>
          <h2 className="platform-credentials-card__title">Textable</h2>
          <p className="platform-credentials-card__desc">
            Platform SMS credentials used for appointment texts and login codes.
          </p>
        </div>
        <span className={`platform-credentials-status ${status.configured ? 'is-connected' : ''}`}>
          {status.configured ? 'Connected' : 'Not connected'}
        </span>
      </div>

      {error && (
        <div className="pkg-dialog-alert pkg-dialog-alert--error" role="alert">
          {error}
        </div>
      )}

      <form className="platform-credentials-form" onSubmit={handleSave}>
        <div className="pkg-dialog-field">
          <label htmlFor="textable-base-url">API base URL</label>
          <input
            id="textable-base-url"
            className="pkg-dialog-input"
            type="url"
            value={form.apiBaseUrl}
            onChange={handleChange('apiBaseUrl')}
            placeholder="https://your-instance.textable.app"
            autoComplete="off"
          />
        </div>
        <div className="pkg-dialog-row">
          <div className="pkg-dialog-field">
            <label htmlFor="textable-user-uid">User UID</label>
            <input
              id="textable-user-uid"
              className="pkg-dialog-input"
              type="password"
              value={form.userUid}
              onChange={handleChange('userUid')}
              placeholder={status.configured ? 'Leave blank to keep the saved UID' : 'Textable user UID'}
              autoComplete="new-password"
            />
          </div>
          <div className="pkg-dialog-field">
            <label htmlFor="textable-api-token">API token</label>
            <input
              id="textable-api-token"
              className="pkg-dialog-input"
              type="password"
              value={form.apiToken}
              onChange={handleChange('apiToken')}
              placeholder={status.configured ? 'Leave blank to keep the saved token' : 'API token'}
              autoComplete="new-password"
            />
          </div>
        </div>
        <div className="pkg-dialog-field">
          <label htmlFor="textable-from-number">From number</label>
          <input
            id="textable-from-number"
            className="pkg-dialog-input"
            type="tel"
            value={form.fromNumber}
            onChange={handleChange('fromNumber')}
            placeholder="+15551234567"
            autoComplete="off"
          />
        </div>
        <div className="platform-credentials-actions">
          <button type="submit" className="pkg-btn pkg-btn-primary" disabled={loading}>
            {action === 'saving' ? 'Saving…' : 'Save credentials'}
          </button>
          <button
            type="button"
            className="pkg-btn pkg-btn-secondary"
            onClick={handleTest}
            disabled={loading || !status.configured}
          >
            {action === 'testing' ? 'Sending…' : 'Send test SMS'}
          </button>
          {status.configured && (
            <button
              type="button"
              className="pkg-btn pkg-btn-danger"
              onClick={handleDisconnect}
              disabled={loading}
            >
              {action === 'clearing' ? 'Removing…' : 'Disconnect'}
            </button>
          )}
        </div>
      </form>
    </section>
  );
};

export default TextableCredentialsCard;
